/**
 * Custom Adventure API Service
 * 
 * Handles API calls for custom adventure booking requests and calendar availability
 * Integrates with the backend custom adventure booking system
 * 
 * @author Wildlife Project Team
 * @version 1.0 - Initial implementation for custom adventure workflow
 */

import { apiClient } from '@/lib/apiClient';
import { AxiosError } from 'axios';
import { format } from 'date-fns';

// Types
export interface DateRange {
  from: Date | undefined;
  to: Date | undefined;
}

export interface AvailabilityData {
  date: string;
  availability: 'available' | 'partial' | 'full';
  totalSlots: number;
  availableSlots: number;
  bookingCount: number;
}

export interface CustomAdventureBookingRequest {
  customAdventureId: string;
  businessId: string;
  requestedDateRange: {
    startDate: Date;
    endDate: Date;
    flexibility: 'exact' | 'flexible_1_week' | 'flexible_2_weeks' | 'flexible_1_month';
  };
  selectedOptions: Record<string, any>;
  estimatedPrice: {
    basePrice: number;
    optionsPrice: number;
    totalEstimate: number;
    currency: string;
  };
  priceBreakdown?: Array<{ label: string; amount: number; description?: string }>;
  participants: {
    adults: number;
    children: number;
    students: number;
  };
  customerDetails: {
    name: string;
    email: string;
    phone: string;
    specialRequests?: string;
    preferredContactMethod: 'email' | 'phone' | 'whatsapp';
  };
}

export interface CustomAdventureBookingResponse {
  requestNumber: string;
  status: string;
  estimatedPrice: {
    basePrice: number;
    optionsPrice: number;
    totalEstimate: number;
    currency: string;
  };
  priceBreakdown?: Array<{ label: string; amount: number; description?: string }>;
  requestedDateRange: {
    startDate: Date;
    endDate: Date;
    flexibility: string;
  };
  message: string;
}

export interface BusinessBookingRequest {
  _id: string;
  requestNumber: string;
  status: 'pending' | 'under_review' | 'accepted' | 'declined' | 'modified' | 'cancelled' | 'converted_to_booking';
  customAdventureId: {
    _id: string;
    name: string;
    description: string;
  };
  customerId: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  requestedDateRange: {
    startDate: Date;
    endDate: Date;
    flexibility: string;
  };
  selectedOptions: Record<string, any>;
  estimatedPrice: {
    basePrice: number;
    optionsPrice: number;
    totalEstimate: number;
    currency: string;
  };
  participants: {
    adults: number;
    children: number;
    students: number;
    totalCount: number;
  };
  customerDetails: {
    name: string;
    email: string;
    phone: string;
    specialRequests?: string;
    preferredContactMethod: string;
  };
  businessResponse?: {
    respondedAt: Date;
    response: 'accept' | 'decline' | 'modify';
    message?: string;
    proposedModifications?: any;
  };
  metadata: {
    createdAt: Date;
    updatedAt: Date;
  };
}

/**
 * Get calendar availability data for a business
 */
export const getBusinessAvailability = async (
  businessId: string,
  startDate: Date,
  endDate: Date
): Promise<AvailabilityData[]> => {
  try {
    const response = await apiClient.get(
      `/business/slots/availability`,
      {
        params: {
          businessId,
          startDate: format(startDate, 'yyyy-MM-dd'),
          endDate: format(endDate, 'yyyy-MM-dd')
        }
      }
    );

    if (!response.data.success) {
      throw new Error('Failed to fetch availability data');
    }

    // Transform the response to match our AvailabilityData interface
    return response.data.data.map((item: any) => ({
      date: item.date,
      availability: item.totalSlots === 0 ? 'full' : 
                   item.availableSlots < item.totalSlots * 0.3 ? 'partial' : 'available',
      totalSlots: item.totalSlots,
      availableSlots: item.availableSlots,
      bookingCount: item.totalSlots - item.availableSlots
    }));

  } catch (error) {
    console.error('Error fetching business availability:', error);
    // Return mock data for development
    return generateMockAvailabilityData(startDate, endDate);
  }
};

/**
 * Create a custom adventure booking request
 */
export const createCustomAdventureBookingRequest = async (
  requestData: CustomAdventureBookingRequest
): Promise<CustomAdventureBookingResponse> => {
  try {
    const response = await apiClient.post('/custom-adventure-bookings', requestData);

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to create booking request');
    }

    return response.data.data;

  } catch (error) {
    if (error instanceof AxiosError) {
      console.error('Error creating booking request:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      if (error.response?.status === 400) {
        throw new Error(error.response.data.error || 'Invalid request data');
      }
      
      if (error.response?.status === 404) {
        throw new Error('Custom adventure not found or not available');
      }
    }
    
    throw new Error('Failed to create booking request. Please try again.');
  }
};

/**
 * Get public status of a booking request
 */
export const getPublicBookingRequestStatus = async (
  requestNumber: string
): Promise<any> => {
  try {
    const response = await apiClient.get(`/custom-adventure-bookings/public/${requestNumber}`);

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to fetch request status');
    }

    return response.data.data;

  } catch (error) {
    if (error instanceof AxiosError) {
      if (error.response?.status === 404) {
        throw new Error('Booking request not found');
      }
    }
    
    throw new Error('Failed to fetch request status');
  }
};

/**
 * Business API: Get all booking requests for the authenticated business
 */
export const getBusinessBookingRequests = async (filters?: {
  status?: string;
  startDate?: Date;
  endDate?: Date;
  customAdventureId?: string;
  page?: number;
  limit?: number;
}): Promise<{
  requests: BusinessBookingRequest[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
  statistics: any;
}> => {
  try {
    const params: any = {};
    
    if (filters?.status) params.status = filters.status;
    if (filters?.startDate) params.startDate = format(filters.startDate, 'yyyy-MM-dd');
    if (filters?.endDate) params.endDate = format(filters.endDate, 'yyyy-MM-dd');
    if (filters?.customAdventureId) params.customAdventureId = filters.customAdventureId;
    if (filters?.page) params.page = filters.page;
    if (filters?.limit) params.limit = filters.limit;

    const response = await apiClient.get('/custom-adventure-bookings/business', { params });

    if (!response.data.success) {
      throw new Error('Failed to fetch booking requests');
    }

    return response.data.data;

  } catch (error) {
    console.error('Error fetching business booking requests:', error);
    throw new Error('Failed to fetch booking requests');
  }
};

/**
 * Business API: Get a specific booking request
 */
export const getBusinessBookingRequest = async (
  requestId: string
): Promise<BusinessBookingRequest> => {
  try {
    const response = await apiClient.get(`/custom-adventure-bookings/business/${requestId}`);

    if (!response.data.success) {
      throw new Error('Failed to fetch booking request');
    }

    return response.data.data;

  } catch (error) {
    console.error('Error fetching booking request:', error);
    throw new Error('Failed to fetch booking request');
  }
};

/**
 * Business API: Respond to a booking request
 */
export const respondToBookingRequest = async (
  requestId: string,
  responseData: {
    response: 'accept' | 'decline' | 'modify';
    message?: string;
    proposedModifications?: any;
  }
): Promise<BusinessBookingRequest> => {
  try {
    const response = await apiClient.post(
      `/custom-adventure-bookings/business/${requestId}/respond`,
      responseData
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to respond to booking request');
    }

    return response.data.data;

  } catch (error) {
    console.error('Error responding to booking request:', error);
    throw new Error('Failed to respond to booking request');
  }
};

/**
 * Business API: Update booking request status
 */
export const updateBookingRequestStatus = async (
  requestId: string,
  status: string,
  reason?: string
): Promise<BusinessBookingRequest> => {
  try {
    const response = await apiClient.patch(
      `/custom-adventure-bookings/business/${requestId}/status`,
      { status, reason }
    );

    if (!response.data.success) {
      throw new Error('Failed to update request status');
    }

    return response.data.data;

  } catch (error) {
    console.error('Error updating request status:', error);
    throw new Error('Failed to update request status');
  }
};

/**
 * Business API: Add communication message
 */
export const addCommunicationMessage = async (
  requestId: string,
  messageData: {
    type: 'email' | 'sms' | 'whatsapp' | 'system';
    direction: 'to_customer' | 'from_customer' | 'internal';
    content: string;
  }
): Promise<BusinessBookingRequest> => {
  try {
    const response = await apiClient.post(
      `/custom-adventure-bookings/business/${requestId}/messages`,
      messageData
    );

    if (!response.data.success) {
      throw new Error('Failed to add message');
    }

    return response.data.data;

  } catch (error) {
    console.error('Error adding message:', error);
    throw new Error('Failed to add message');
  }
};

/**
 * Business API: Get booking request statistics
 */
export const getBookingRequestStatistics = async (): Promise<any> => {
  try {
    const response = await apiClient.get('/custom-adventure-bookings/business/statistics');

    if (!response.data.success) {
      throw new Error('Failed to fetch statistics');
    }

    return response.data.data;

  } catch (error) {
    console.error('Error fetching statistics:', error);
    throw new Error('Failed to fetch statistics');
  }
};

/**
 * Generate mock availability data for development
 */
function generateMockAvailabilityData(startDate: Date, endDate: Date): AvailabilityData[] {
  const data: AvailabilityData[] = [];
  const current = new Date(startDate);
  
  while (current <= endDate) {
    const dayOfWeek = current.getDay();
    const totalSlots = dayOfWeek === 0 || dayOfWeek === 6 ? 6 : 8; // Fewer slots on weekends
    
    // Random availability simulation
    const random = Math.random();
    let availableSlots: number;
    let availability: 'available' | 'partial' | 'full';
    
    if (random < 0.3) {
      // 30% chance of being full
      availableSlots = 0;
      availability = 'full';
    } else if (random < 0.6) {
      // 30% chance of being partial
      availableSlots = Math.floor(totalSlots * 0.3);
      availability = 'partial';
    } else {
      // 40% chance of being available
      availableSlots = Math.floor(totalSlots * 0.7) + Math.floor(Math.random() * Math.floor(totalSlots * 0.3));
      availability = 'available';
    }
    
    data.push({
      date: format(current, 'yyyy-MM-dd'),
      availability,
      totalSlots,
      availableSlots,
      bookingCount: totalSlots - availableSlots
    });
    
    current.setDate(current.getDate() + 1);
  }
  
  return data;
} 